<template>
  <article class="guest-signup">
    <section>
      <h2>{{t.title}}</h2>
      <div class="subtitle">{{t.subtitle}}</div>
    </section>
    <section class="signin-link" v-show="!hideSignin">
      <p>{{t.alreadyCustomer}} <a :href="loginUrl">{{t.signIn}}</a></p>
    </section>
    <section>
      <v-form ref="form" v-model="valid" lazy-validation>
        <v-radio-group column v-model="radios" :mandatory="true" v-show="enableCreateAccount">
          <v-radio :label="t.checkoutAsAGuest" value="guest"></v-radio>
          <v-radio :label="t.createAnAccount" value="create"></v-radio>
          <span class="radio-sub-message" @click="setAccountChecked()">{{t.saveOrderHistory}}</span>
        </v-radio-group>
        <v-text-field
          :label="t.firstName"
          v-model="guest.firstName"
          :rules="nameRules"
          required></v-text-field>
          <v-text-field
          :label="t.lastName"
          v-model="guest.lastName"
          :rules="nameRules"
          required></v-text-field>
        <v-text-field
          :label="t.email"
          v-model="guest.email"
          :rules="emailRules"
          validate-on-blur
          :error-messages="emailErrors"
          required></v-text-field>
        <v-text-field
          v-if="showTaxId"
          :label="t.taxId"
          v-model="guest.govtID"
          v-on:keydown="preventSpaces"
          :rules="taxIdRules"
          required></v-text-field>
        <div v-if="createAccountChecked()">
          <password
            :password="guest.password"
            v-model="guest.password"
            @passwordUpdated="updatedPassword"></password>
          <v-checkbox v-model="agreeToTerms" :rules="agreeToTermsRules" required>
            <template v-slot:label>
              <div v-html="t.agree2Terms"></div>
            </template>
          </v-checkbox>
          <v-checkbox v-model="guest.emailOptIn" :label="t.contactMe"></v-checkbox>
        </div>
      </v-form>
      <button
        class="primary-hit fluid"
        @click="create()"
        v-if="!createAccountChecked()"
        :disabled="!valid || formErrorExists || processing"
      >{{t.continueAsGuest}}</button>
      <button
        class="primary-hit fluid"
        @click="create()"
        v-if="createAccountChecked()"
        :disabled="!valid || formErrorExists || processing"
      >{{t.continueToCheckout}}</button>
      <p v-if="showError" class="unknown-error v-messages error--text">
        {{t.unknownError}}
      </p>
    </section>
    <loader v-show="processing"></loader>
  </article>
</template>

<script>
import { ConfigService, RunConfigService, SponsorStorageService, Validate, util, storage, events } from '@nuskin/ns-util';
import { AuthenticationService } from '@nuskin/ns-account';
import { CartService } from '@nuskin/ns-shop';
import { mapActions, mapState } from 'vuex';
import Password from '../components/Password';
import Loader from '../components/Loader';
export default {
  name: "GuestSignup",
  components: {
    Password,
    Loader
  },
  data() {
    return {
      valid: false,
      showError: false,
      guest: {},
      emailErrors: [],
      nameRules: [v => !!v || this.t.fieldRequired],
      emailRules: [
        v => !!v || this.t.fieldRequired,
        v => Validate.regexes.email.test(v) || this.t.invalidEmail
      ],
      radios: "guest",
      agreeToTerms: false,
      agreeToTermsRules: [v => !!v || this.t.fieldRequired],
      futureContact: false,
      config: ConfigService.getMarketConfig(),
      runConfig: RunConfigService.getRunConfig(),
      taxIdType: "",
      taxIdRules: [v => !!v || this.t.fieldRequired],
      showTaxId: false,
      sponsor: SponsorStorageService.getSponsor(),
      formErrorExists: false,
      destination: "",
      loginUrl: '/content/login/corporate/{language}.html?destination={destination}&cancel={cancel}&market={market}',
      processing: false,
      hideSignin: false,
      enableCreateAccount: false
    };
  },
  async mounted() {
    let runConfig = RunConfigService.getRunConfig();

    await this.$features.$waitForLoad();
    if (this.$features['cx13_6186_storefront_redirect']) {
      // only use mysite url for storefront context
      let isMySite = storage.getItem(storage.keys.SHOPPING_CONTEXT) && storage.getItem(storage.keys.SHOPPING_CONTEXT).context === 'storefront';
      // if url is in cookieVal, then use that
      let cookieVal = storage.getItem(storage.keys.AFTER_LOGIN_URL);
      this.destination = cookieVal || CartService.getCheckoutPage({ isMySite: isMySite, route: 'orderSummary' });
    } else {
      this.destination = CartService.getCheckoutPage({ isMySite: true, route: 'orderSummary' });
    }

    storage.removeItem(storage.keys.AFTER_LOGIN_URL);

    if (this.config && this.config.signup && this.config.signup.enableCreateAccount) {
      this.enableCreateAccount = true;
    }

    // If we happen to be logged in then go ahead to checkout
    if (AuthenticationService.isLoggedIn()) {
      window.location = this.destination;
    }

    // Build login url
    this.loginUrl = runConfig.baseUrl + this.loginUrl.replace('{language}', runConfig.language)
      .replace('{market}', runConfig.country)
      .replace('{destination}', this.destination)
      .replace('{cancel}', document.referrer);

    // Check to make sure we have a sponsor if we don't go to the login page
    if (!this.sponsor) {
      window.location = this.loginUrl;
    }

    if (this.config.signup) {
      this.showTaxId = this.config.signup.governmentIDOptions !== "NONE" && this.config.signup.governmentIDOptions !== undefined;
      this.taxIdType = this.showTaxId ? this.config.signup.governmentIDOptions : "";
    }

    if (CartService.containsAdrItems()) {
      this.enableCreateAccount = false;
      this.radios = "create";
    }

    util.isPersonalOffer() ? this.hideSignin = true : this.hideSignin = false;
  },
  computed: {
    ...mapState('translations', ['t']),
    formComplete: function() {
      return this.createAccountChecked() ? (this.guest.firstName && this.guest.lastName && this.guest.email && this.guest.password && this.agreeToTerms) : (this.guest.firstName && this.guest.lastName && this.guest.email);
    }
  },
  methods: {
    ...mapActions('user', ['guestSignup', 'accountExists']),
    async create() {
      this.showError = false;
      // Check for valid input
      if (this.$refs.form.validate()) {
        // Finally, validate the email using the email validation service.
        const emailIsValid = await Validate.email(this.guest.email);
        if (!emailIsValid) {
          this.emailErrors.push(this.t.invalidEmail);
          return;
        }

        // If we are creating an account check to see if an account exists with the email
        if (this.createAccountChecked()) {
          if (await this.accountExists(this.guest.email)) {
            this.emailErrors.push(this.t.accountExistsWithEmail);
          }
        }
        // If we don't have an error with the email create the account
        if (this.emailErrors.length === 0) {
          this.processing = true;
          let user = await this.guestSignup(this.guest);
          if (user) {
            if (this.createAccountChecked()) {
              this.loyaltyCheck();
            } else {
              let isMysite = true;

              if (this.$features['cx13_6186_storefront_redirect']) {
              // Go to checkout, only use mysite url for storefront context
                isMysite = storage.getItem(storage.keys.SHOPPING_CONTEXT) && storage.getItem(storage.keys.SHOPPING_CONTEXT).context === 'storefront';
              }
              CartService.goToCheckoutPage({ isMySite: isMysite, route: 'orderSummary' });

              this.processing = false;
              // Display Error message
              this.showError = true;
            }
          }
        }
      }
    },

    redirectFlow() {
      let isMysite = true;

      if (this.$features['cx13_6186_storefront_redirect']) {
        // Go to checkout, only use mysite url for storefront context
        isMysite = storage.getItem(storage.keys.SHOPPING_CONTEXT) && storage.getItem(storage.keys.SHOPPING_CONTEXT).context === 'storefront';
      }
      CartService.goToCheckoutPage({ isMySite: isMysite, route: 'orderSummary' });

      this.processing = false;
      // Display Error message
      this.showError = true;
    },

    loyaltyCheck() {
      if (
        this.config &&
        this.config.mecSite &&
        this.config.mecSite.loyaltyEnabled &&
        document.body.contains(document.getElementById('loyalty_signup'))
      ) {
        events.publish(events.loyalty.LOYALTY_SIGNUP_OPEN, {});
        events.subscribe(events.loyalty.LOYALTY_OPT_IN_CLOSED, (loyaltyObj) => {
          this.redirectFlow();
        });
      } else {
        this.redirectFlow();
      }
    },

    checkPasswordValidity() {
      let totalCompleted = 0;
      if (this.guest && this.guest.password) {
        this.guest.password.match(/(?=.{8,})/) ? totalCompleted++ : totalCompleted += 0;
        this.guest.password.match(/(?=.*[A-Z])/) ? totalCompleted++ : totalCompleted += 0;
        this.guest.password.match(/(?=.*[a-z])/) ? totalCompleted++ : totalCompleted += 0;
        this.guest.password.match(/(?=.*[0-9])/) ? totalCompleted++ : totalCompleted += 0;
        this.guest.password.match(/(?=.*[@#$%^&*()_+?~!])/) ? totalCompleted++ : totalCompleted += 0;
      }
      return totalCompleted < 5;
    },
    preventSpaces(event) {
      if (event.keyCode === 32) {
        event.preventDefault();
      }
    },
    createAccountChecked() {
      return this.radios === "create";
    },
    setAccountChecked() {
      this.radios = "create";
    },
    updatedPassword(str) {
      this.$set(this.guest, 'password', str);
    }
  },
  watch: {
    'guest.email': function (val) {
      this.emailErrors = [];
    },
    'guest.password': function () {
      if (this.createAccountChecked()) {
        this.checkPasswordValidity() ? this.formErrorExists = true : this.formErrorExists = false;
      }
    },
    valid() {
      if (this.createAccountChecked()) {
        this.checkPasswordValidity() ? this.formErrorExists = true : this.formErrorExists = false;
      } else {
        this.valid === false ? this.formErrorExists = true : this.formErrorExists = false;
      }
    }
  }
};
</script>

<style lang="scss">
@import "~@nuskin/ns-core-styles/src/scss/global.mixins";
@import "~@nuskin/ns-core-styles/src/scss/typography";
@import "~@nuskin/ns-core-styles/src/scss/forms";
@import "~@nuskin/ns-core-styles/src/scss/buttons";
@import "~@nuskin/ns-core-styles/src/scss/vuetifyOverrides";
@import url('https://fonts.googleapis.com/icon?family=Material+Icons');

.guest-signup {
  * {
    box-sizing: border-box;
  }

  h2 {
    margin-bottom: 10px;
  }

  .subtitle {
    @include subtitle();
  }

  section {
    max-width: 400px;
    margin: 0 auto 30px auto;
  }

  .signin-link {
    border-bottom: 1px solid map-get($baseColors, ash);
    padding: 20px 0;
  }

  p {
    @include p-medium();
    margin-bottom: 0;
  }

  a {
    @include a-base();
    color: map-get($themeColors, color-expressive-6);
    text-decoration: none;

    &:hover {
      color: map-get($themeColors, color-interactive-3);
    }
  }

  .v-input div a {
    font-size: 16px;
  }

  .v-label div, .v-label div p {
    color: map-get($baseColors, blackhole);
    font-weight: map-get($typography, font-weight-normal);
  }

  .unknown-error {
    padding-top: 15px;
  }

  @media screen and (max-width: 480px) {
    padding: 20px;
  }
}
</style>
